/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║                                                                           ║
 * ║                    🧪 TEST MODE CONFIGURATION 🧪                          ║
 * ║                                                                           ║
 * ║  Configuration types and presets for Test Mode gameplay.                  ║
 * ║  Allows customization of map, NPCs, powerups, food, and shrinking mode.   ║
 * ║                                                                           ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

import { MapShape, MapSize } from './world-shape';
import { PowerupType, FoodCategory, POWERUP_RATE_PRESETS, type PowerupRatePreset, VISIBILITY_PRESETS, type VisibilityPreset } from './constants';
import type { PersonalityType } from './ai';

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                        SHRINKING MODE CONFIG                              ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Shrinking zone phase
 */
export enum ShrinkingPhase {
  /** Waiting before first shrink */
  WAITING = 'waiting',
  /** Warning countdown before shrink */
  WARNING = 'warning',
  /** Actively shrinking */
  SHRINKING = 'shrinking',
  /** Stable between shrinks */
  STABLE = 'stable',
  /** Finished shrinking (at minimum size) */
  FINISHED = 'finished',
}

/**
 * Shrinking mode configuration
 */
export interface ShrinkingModeConfig {
  /** Whether shrinking mode is enabled */
  enabled: boolean;
  /** Initial delay before first shrink (seconds) */
  initialDelay: number;
  /** Warning duration before each shrink (seconds) */
  warningDuration: number;
  /** Time between shrinks (seconds) */
  shrinkInterval: number;
  /** How long each shrink takes (seconds) */
  shrinkDuration: number;
  /** Minimum area percentage (0-1) */
  minimumAreaPercent: number;
  /** Damage per second when outside safe zone */
  damagePerSecond: number;
  /** Number of shrink phases */
  shrinkPhases: number;
}

/**
 * Default shrinking mode configuration
 */
export const DEFAULT_SHRINKING_CONFIG: ShrinkingModeConfig = {
  enabled: false,
  initialDelay: 60,
  warningDuration: 10,
  shrinkInterval: 30,
  shrinkDuration: 15,
  minimumAreaPercent: 0.20,
  damagePerSecond: 5,
  shrinkPhases: 5,
};

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                          NPC CONFIGURATION                                ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * NPC spawning configuration
 */
export interface NPCConfig {
  /** Number of NPCs to spawn */
  count: number;
  /** Which personality types are enabled */
  enabledPersonalities: Set<PersonalityType>;
  /** Whether LEVIATHAN can spawn */
  allowLeviathan: boolean;
}

/**
 * Default NPC configuration
 */
export const DEFAULT_NPC_CONFIG: NPCConfig = {
  count: 2,
  enabledPersonalities: new Set(['Collector', 'Hunter', 'Apex'] as PersonalityType[]),
  allowLeviathan: false,
};

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                       TEST MODE CONFIGURATION                             ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Complete test mode configuration
 */
export interface TestModeConfig {
  /** Map shape (circular or rectangular) */
  mapShape: MapShape;
  /** Map size preset */
  mapSize: MapSize;
  /** NPC configuration */
  npcConfig: NPCConfig;
  /** Which powerup types are enabled */
  enabledPowerups: Set<PowerupType>;
  /** Which food categories are enabled */
  enabledFoodCategories: Set<FoodCategory>;
  /** Shrinking mode configuration (rectangular maps only) */
  shrinkingConfig: ShrinkingModeConfig;
  /** Powerup spawn rate preset */
  powerupRatePreset: PowerupRatePreset;
  /** Visibility/fog of war preset */
  visibilityPreset: VisibilityPreset;
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                            PRESETS                                        ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * All powerup types for preset creation
 */
const ALL_POWERUPS = new Set<PowerupType>([
  PowerupType.MULTIPLIER_2X,
  PowerupType.MULTIPLIER_4X,
  PowerupType.MULTIPLIER_6X,
  PowerupType.MAGNET,
  PowerupType.SPEED,
  PowerupType.AGILITY,
  PowerupType.VISION,
]);

/**
 * Combat-focused powerups for battle royale
 */
const COMBAT_POWERUPS = new Set<PowerupType>([
  PowerupType.SPEED,
  PowerupType.AGILITY,
  PowerupType.VISION,
]);

/**
 * All food categories
 */
const ALL_FOOD_CATEGORIES = new Set<FoodCategory>([
  FoodCategory.JUNK,
  FoodCategory.REGULAR,
  FoodCategory.HEALTHY,
]);

/**
 * All personality types (excluding Legendary)
 */
const ALL_PERSONALITIES = new Set<PersonalityType>(['Collector', 'Hunter', 'Apex']);

/**
 * Test mode presets
 */
export enum TestModePreset {
  CLASSIC = 'classic',
  NIGHTMARE = 'nightmare',
  BATTLE_ROYALE = 'battle_royale',
  PEACEFUL = 'peaceful',
  CUSTOM = 'custom',
}

/**
 * Preset configurations
 */
export const TEST_MODE_PRESETS: Record<Exclude<TestModePreset, TestModePreset.CUSTOM>, TestModeConfig> = {
  /**
   * Classic - Standard gameplay experience
   * Circular/Medium, 2 NPCs, all items
   */
  [TestModePreset.CLASSIC]: {
    mapShape: MapShape.CIRCULAR,
    mapSize: MapSize.MEDIUM,
    npcConfig: {
      count: 2,
      enabledPersonalities: new Set(ALL_PERSONALITIES),
      allowLeviathan: false,
    },
    enabledPowerups: new Set(ALL_POWERUPS),
    enabledFoodCategories: new Set(ALL_FOOD_CATEGORIES),
    shrinkingConfig: { ...DEFAULT_SHRINKING_CONFIG, enabled: false },
    powerupRatePreset: POWERUP_RATE_PRESETS.NORMAL!,
    visibilityPreset: VISIBILITY_PRESETS.NORMAL!,
  },

  /**
   * Nightmare - Maximum challenge
   * Circular/Large, 10 NPCs + LEVIATHAN, all items
   */
  [TestModePreset.NIGHTMARE]: {
    mapShape: MapShape.CIRCULAR,
    mapSize: MapSize.LARGE,
    npcConfig: {
      count: 10,
      enabledPersonalities: new Set(ALL_PERSONALITIES),
      allowLeviathan: true,
    },
    enabledPowerups: new Set(ALL_POWERUPS),
    enabledFoodCategories: new Set(ALL_FOOD_CATEGORIES),
    shrinkingConfig: { ...DEFAULT_SHRINKING_CONFIG, enabled: false },
    powerupRatePreset: POWERUP_RATE_PRESETS.ABUNDANT!,
    visibilityPreset: VISIBILITY_PRESETS.LIMITED!,
  },

  /**
   * Battle Royale - Shrinking zone combat
   * Rectangular/Large, shrinking enabled, combat powerups only
   */
  [TestModePreset.BATTLE_ROYALE]: {
    mapShape: MapShape.RECTANGULAR,
    mapSize: MapSize.LARGE,
    npcConfig: {
      count: 8,
      enabledPersonalities: new Set(['Hunter', 'Apex'] as PersonalityType[]),
      allowLeviathan: false,
    },
    enabledPowerups: new Set(COMBAT_POWERUPS),
    enabledFoodCategories: new Set(ALL_FOOD_CATEGORIES),
    shrinkingConfig: {
      enabled: true,
      initialDelay: 60,
      warningDuration: 10,
      shrinkInterval: 30,
      shrinkDuration: 15,
      minimumAreaPercent: 0.15,
      damagePerSecond: 5,
      shrinkPhases: 6,
    },
    powerupRatePreset: POWERUP_RATE_PRESETS.SCARCE!,
    visibilityPreset: VISIBILITY_PRESETS.DENSE_FOG!,
  },

  /**
   * Peaceful - Relaxed exploration
   * Circular/Small, 0 NPCs, all food
   */
  [TestModePreset.PEACEFUL]: {
    mapShape: MapShape.CIRCULAR,
    mapSize: MapSize.SMALL,
    npcConfig: {
      count: 0,
      enabledPersonalities: new Set<PersonalityType>(),
      allowLeviathan: false,
    },
    enabledPowerups: new Set(ALL_POWERUPS),
    enabledFoodCategories: new Set(ALL_FOOD_CATEGORIES),
    shrinkingConfig: { ...DEFAULT_SHRINKING_CONFIG, enabled: false },
    powerupRatePreset: POWERUP_RATE_PRESETS.ABUNDANT!,
    visibilityPreset: VISIBILITY_PRESETS.FULL_VISION!,
  },
};

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                         UTILITY FUNCTIONS                                 ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Get default test mode config (Classic preset)
 */
export function getDefaultTestModeConfig(): TestModeConfig {
  return cloneTestModeConfig(TEST_MODE_PRESETS[TestModePreset.CLASSIC]);
}

/**
 * Get test mode config from preset
 */
export function getTestModePreset(preset: TestModePreset): TestModeConfig {
  if (preset === TestModePreset.CUSTOM) {
    return getDefaultTestModeConfig();
  }
  return cloneTestModeConfig(TEST_MODE_PRESETS[preset]);
}

/**
 * Deep clone a test mode config (Sets need special handling)
 */
export function cloneTestModeConfig(config: TestModeConfig): TestModeConfig {
  return {
    mapShape: config.mapShape,
    mapSize: config.mapSize,
    npcConfig: {
      count: config.npcConfig.count,
      enabledPersonalities: new Set(config.npcConfig.enabledPersonalities),
      allowLeviathan: config.npcConfig.allowLeviathan,
    },
    enabledPowerups: new Set(config.enabledPowerups),
    enabledFoodCategories: new Set(config.enabledFoodCategories),
    shrinkingConfig: { ...config.shrinkingConfig },
    powerupRatePreset: { ...config.powerupRatePreset },
    visibilityPreset: { ...config.visibilityPreset },
  };
}

/**
 * Serialize test mode config to JSON-compatible object
 * (Sets are converted to arrays for storage)
 */
export function serializeTestModeConfig(config: TestModeConfig): object {
  return {
    mapShape: config.mapShape,
    mapSize: config.mapSize,
    npcConfig: {
      count: config.npcConfig.count,
      enabledPersonalities: Array.from(config.npcConfig.enabledPersonalities),
      allowLeviathan: config.npcConfig.allowLeviathan,
    },
    enabledPowerups: Array.from(config.enabledPowerups),
    enabledFoodCategories: Array.from(config.enabledFoodCategories),
    shrinkingConfig: config.shrinkingConfig,
    powerupRatePreset: config.powerupRatePreset,
  };
}

/**
 * Deserialize test mode config from JSON-compatible object
 */
export function deserializeTestModeConfig(data: Record<string, unknown>): TestModeConfig {
  const npcConfig = data.npcConfig as Record<string, unknown>;
  return {
    mapShape: data.mapShape as MapShape,
    mapSize: data.mapSize as MapSize,
    npcConfig: {
      count: npcConfig.count as number,
      enabledPersonalities: new Set(npcConfig.enabledPersonalities as PersonalityType[]),
      allowLeviathan: npcConfig.allowLeviathan as boolean,
    },
    enabledPowerups: new Set(data.enabledPowerups as PowerupType[]),
    enabledFoodCategories: new Set(data.enabledFoodCategories as FoodCategory[]),
    shrinkingConfig: data.shrinkingConfig as ShrinkingModeConfig,
    powerupRatePreset: data.powerupRatePreset as PowerupRatePreset,
    visibilityPreset: data.visibilityPreset as VisibilityPreset,
  };
}

/**
 * Check if shrinking mode can be enabled for current config
 * (Only available for rectangular maps)
 */
export function canEnableShrinking(config: TestModeConfig): boolean {
  return config.mapShape === MapShape.RECTANGULAR;
}

/**
 * Validate and fix test mode config
 */
export function validateTestModeConfig(config: TestModeConfig): TestModeConfig {
  const validated = cloneTestModeConfig(config);

  // Disable shrinking for circular maps
  if (validated.mapShape === MapShape.CIRCULAR) {
    validated.shrinkingConfig.enabled = false;
  }

  // Ensure at least one food category is enabled
  if (validated.enabledFoodCategories.size === 0) {
    validated.enabledFoodCategories.add(FoodCategory.REGULAR);
  }

  // Clamp NPC count
  validated.npcConfig.count = Math.max(0, Math.min(20, validated.npcConfig.count));

  return validated;
}
